import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../i18n/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SlideBg({ imageId, fallbackIdx, hasOverlayText }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!imageId) return;
    let cancelled = false;
    fetch(`${API}/images/${imageId}`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setSrc(j.data_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [imageId]);
  const gradients = [
    "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)",
    "linear-gradient(135deg, #1B6FE0 0%, #0F2A6E 100%)",
    "linear-gradient(135deg, #0F2A6E 0%, #3B82F6 60%, #0F2A6E 100%)",
    "linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)",
    "linear-gradient(135deg, #0B1F4D 0%, #1B6FE0 100%)",
  ];
  return (
    <div className="absolute inset-0 overflow-hidden">
      {src ? (
        <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{ background: gradients[fallbackIdx % gradients.length] }} />
      )}
      {/* Darken overlay only when overlay text/CTA is rendered on top of the slide */}
      {hasOverlayText && (
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(15,42,110,0.78) 0%, rgba(15,42,110,0.45) 65%, rgba(15,42,110,0.2) 100%)" }} />
      )}
      {!src && <div className="absolute inset-0 grid-bg opacity-15" />}
    </div>
  );
}

export default function Hero() {
  const { isRTL } = useI18n();
  const { pg } = useSiteContent();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/slides`).then((r) => { if (!cancelled) setSlides(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fallback content from page-content if no slides exist
  const fallback = slides.length === 0 ? [{
    title: pg("hero.headline", "Powering enterprises across MENA"),
    subtitle: pg("hero.sub", "Infrastructure, security, automation and AI — under one trusted partner."),
    cta_text: pg("hero.cta_primary", "Explore brands"),
    cta_link: "#products",
  }] : slides;

  // Auto-rotate every 5s
  useEffect(() => {
    if (fallback.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % fallback.length), 5000);
    return () => clearInterval(t);
  }, [fallback.length]);

  const scrollTo = (href) => {
    if (!href) return;
    if (href.startsWith("#")) {
      document.getElementById(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden w-full hero-responsive"
    >
      {fallback.map((s, i) => {
        const hasTitle = !!(s.title && String(s.title).trim());
        const hasOverlayText = hasTitle; // only darken when there's text on top
        return (
        <div
          key={s.id || i}
          data-testid={`hero-slide-${i}`}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
          aria-hidden={i !== idx}
        >
          <SlideBg imageId={s.image_id} fallbackIdx={i} hasOverlayText={hasOverlayText} />
          {hasTitle ? (
            <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-16 md:py-20 h-full flex flex-col justify-center">
              <div className="max-w-3xl">
                <h1 className="font-heading font-semibold text-white text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-[-0.02em] text-balance">
                  {s.title}
                </h1>
                {s.subtitle && (
                  <p className="mt-6 text-[17px] md:text-[19px] text-slate-100/95 max-w-2xl leading-relaxed font-light">
                    {s.subtitle}
                  </p>
                )}
                {s.cta_text && (
                  <div className="mt-10 flex flex-wrap gap-3">
                    <Button
                      data-testid={`hero-slide-cta-${i}`}
                      onClick={() => scrollTo(s.cta_link)}
                      className="h-12 px-6 text-[14.5px] text-white hover:brightness-110 font-medium"
                      style={{ background: "#1B6FE0" }}
                    >
                      {s.cta_text}
                      <ArrowRight size={16} weight="bold" className={isRTL ? "rtl-flip" : ""} />
                    </Button>
                    <Button
                      onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}
                      variant="outline"
                      className="h-12 px-6 text-[14.5px] bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white hover:border-white font-medium"
                    >
                      Contact us
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : s.cta_text ? (
            /* Image-only slide — CTA aligned below the title text baked into the image */
            <div className="absolute bottom-16 md:bottom-20 left-0 right-0 z-10 px-6 md:px-10 lg:px-16 xl:px-24">
              <Button
                data-testid={`hero-slide-cta-${i}`}
                onClick={() => scrollTo(s.cta_link)}
                className="h-11 px-6 text-[13.5px] text-white hover:brightness-110 font-medium shadow-xl"
                style={{ background: "#1B6FE0" }}
              >
                {s.cta_text}
                <ArrowRight size={15} weight="bold" className={isRTL ? "rtl-flip" : ""} />
              </Button>
            </div>
          ) : null}
        </div>
        );
      })}

      {/* Slide indicators */}
      {fallback.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-indicators">
          {fallback.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-dot-${i}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === idx ? 32 : 12,
                background: i === idx ? "#1B6FE0" : "rgba(255,255,255,0.5)",
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
